import { useState, useCallback, useEffect, useRef } from 'react';
import { Head, Link } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { CancelOrderModal } from '../../components/orders/CancelOrderModal';
import { useOrderRealtime } from '../../hooks/useOrderRealtime';
import { getStatusStyle } from '../../utils/orderStatus';
import { formatPrice } from '../../utils/format';
import { getCSRFToken } from '../../utils/csrf';
import { route } from '../../utils/route';
import type { OrderDetail, StatusHistoryEntry } from '../../types/orders';
import {
  ArrowLeft, Download, RefreshCw, Upload, CreditCard, XCircle,
  MapPin, MessageSquare, Calendar, CalendarPlus, Wallet,
} from 'lucide-react';

interface ShowProps {
  order: OrderDetail;
  pusherKey: string;
  pusherCluster: string;
}

const PENDING_CANCELATION = 5;
const POLL_INTERVAL_MS = 3000;

export default function Show({ order: initialOrder, pusherKey, pusherCluster }: ShowProps) {
  const [order, setOrder] = useState(initialOrder);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Real-time updates
  const pendingCancelationIds = order.status === PENDING_CANCELATION ? [order.id] : [];

  useOrderRealtime(pendingCancelationIds, (orderId, newStatus) => {
    setOrder(prev => ({ ...prev, status: 4, status_name: newStatus, is_user_cancellable: false }));
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, { key: pusherKey, cluster: pusherCluster });

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  const orderStatusUrl = route('api.orders.show_json', { id: order.id });

  const pollOrderStatus = useCallback(() => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(orderStatusUrl, {
          credentials: 'include',
          headers: { 'Accept': 'application/json' },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.status !== PENDING_CANCELATION) {
          setOrder(prev => ({
            ...prev,
            status: data.status,
            status_name: data.status_name,
            is_user_cancellable: false,
          }));
          clearInterval(interval);
          pollingRef.current = null;
        }
      } catch {
        // silently retry
      }
    }, POLL_INTERVAL_MS);
    pollingRef.current = interval;
  }, [orderStatusUrl]);

  const cancelUrl = route('api.orders.cancel');

  const handleCancelConfirm = useCallback(async () => {
    setCancelLoading(true);
    try {
      const formData = new FormData();
      formData.append('id', String(order.id));

      const response = await fetch(cancelUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-XSRF-TOKEN': getCSRFToken(),
          'Accept': 'text/html,application/xhtml+xml',
        },
        body: formData,
      });

      if (response.ok || response.redirected) {
        setOrder(prev => ({
          ...prev,
          status: PENDING_CANCELATION,
          status_name: 'En proceso de cancelación',
          is_user_cancellable: false,
        }));
        pollOrderStatus();
        setCancelModalOpen(false);
      } else {
        throw new Error('Error al cancelar');
      }
    } catch {
      alert('Error al cancelar el pedido. Por favor intentá de nuevo.');
    } finally {
      setCancelLoading(false);
    }
  }, [order.id, cancelUrl, pollOrderStatus]);

  const statusStyle = getStatusStyle(order.status);
  const hasDiscounts = order.discounts.length > 0;
  const surcharges = order.surcharges ?? [];
  const productSubtotal = order.products.reduce((sum, p) => sum + p.total, 0);

  return (
    <MainLayout>
      <Head title={`Pedido #${order.formatted_number}`}>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="mx-auto max-w-4xl px-4 py-6 sm:py-8">
        {/* Back link */}
        <Link
          href={route('orders.index')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-gray-500 no-underline transition-colors hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver a mis compras
        </Link>

        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              Pedido #{order.formatted_number}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Realizado el {order.created_at}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${statusStyle}`}>
              {order.status_name}
            </span>
            <span className="text-xl font-bold text-gray-800 sm:text-2xl">
              {formatPrice(order.total)}
            </span>
          </div>
        </div>

        {/* Status Timeline */}
        {order.status_history.length > 0 && (
          <StatusTimeline history={order.status_history} currentStatus={order.status} />
        )}

        {/* Info section */}
        <div className="mb-6 grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:grid-cols-2 sm:p-5">
          <InfoItem
            icon={<Calendar className="h-4 w-4" />}
            label="Fecha de entrega"
            value={order.human_friendly_delivery_date || order.delivery_date || 'Sin definir'}
          />
          <InfoItem
            icon={<Wallet className="h-4 w-4" />}
            label="Forma de pago"
            value={order.payment_method_name}
          />
          <InfoItem
            icon={<MapPin className="h-4 w-4" />}
            label="Dirección"
            value={order.address}
          />
          {order.comment && (
            <InfoItem
              icon={<MessageSquare className="h-4 w-4" />}
              label="Comentario"
              value={order.comment}
            />
          )}
        </div>

        {/* Products */}
        <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3 sm:px-5">
            <h2 className="text-sm font-semibold text-gray-800">
              Productos ({order.products.length})
            </h2>
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-400">
                  <th className="px-5 py-2.5">Producto</th>
                  <th className="px-5 py-2.5">Formato</th>
                  <th className="px-5 py-2.5 text-right">Cant.</th>
                  <th className="px-5 py-2.5 text-right">Precio unit.</th>
                  <th className="px-5 py-2.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.products.map((p) => (
                  <tr key={p.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-5 py-2.5 text-gray-800">{p.product_name}</td>
                    <td className="px-5 py-2.5 text-gray-500">{p.format_name || '—'}</td>
                    <td className="px-5 py-2.5 text-right text-gray-800">{p.quantity}</td>
                    <td className="px-5 py-2.5 text-right text-gray-500">{formatPrice(p.price)}</td>
                    <td className="px-5 py-2.5 text-right font-medium text-gray-800">{formatPrice(p.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked cards */}
          <div className="divide-y divide-gray-50 sm:hidden">
            {order.products.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800">{p.product_name}</p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {p.format_name ? `${p.format_name} · ` : ''}{p.quantity} × {formatPrice(p.price)}
                  </p>
                </div>
                <span className="ml-3 flex-shrink-0 text-sm font-semibold text-gray-800">
                  {formatPrice(p.total)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal productos</span>
              <span>{formatPrice(productSubtotal)}</span>
            </div>
            {order.delivery_cost > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Envío</span>
                <span>{formatPrice(order.delivery_cost)}</span>
              </div>
            )}
            {order.delivery_cost === 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Envío</span>
                <span className="font-medium text-green-600">Gratis</span>
              </div>
            )}
            {hasDiscounts && (
              <>
                {order.discounts.map((d, i) => (
                  <div key={i} className="flex justify-between text-green-600">
                    <span>{d.label}</span>
                    <span>-{formatPrice(d.amount)}</span>
                  </div>
                ))}
              </>
            )}
            {surcharges.map((s, i) => (
              <div key={i} className="flex justify-between text-gray-700">
                <span>{s.label}</span>
                <span>+{formatPrice(s.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-gray-200 pt-2 text-base font-bold text-gray-900">
              <span>Total</span>
              <span>{formatPrice(order.total)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap sm:gap-3">
          <a
            href={route('orders.pdf', { pedido: order.id })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 no-underline transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            <Download className="h-4 w-4" />
            {order.has_invoice ? 'Descargar factura' : 'Descargar remito'}
          </a>
          <a
            href={route('orders.repeat_by_id', { id: order.id })}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 no-underline transition-colors hover:bg-gray-50 hover:text-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
            Repetir pedido
          </a>
          {order.delivery_date_iso && order.status === 2 && (
            <a
              href={route('orders.reminder', { pedido: order.id })}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 no-underline transition-colors hover:bg-gray-50 hover:text-gray-700"
            >
              <CalendarPlus className="h-4 w-4" />
              Recordatorio
            </a>
          )}
          {order.is_transfer_pending && (
            <a
              href={route('transfer_receipt.show', { id: order.id })}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white no-underline transition-colors hover:bg-green-700 hover:text-white"
            >
              <Upload className="h-4 w-4" />
              Subir comprobante
            </a>
          )}
          {order.can_be_paid_online && (
            <a
              href={route('orders.pay', { id: order.id })}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white no-underline transition-colors hover:bg-green-700 hover:text-white"
            >
              <CreditCard className="h-4 w-4" />
              Cambiar forma de pago
            </a>
          )}
          {order.is_user_cancellable && (
            <button
              type="button"
              onClick={() => setCancelModalOpen(true)}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
            >
              <XCircle className="h-4 w-4" />
              Cancelar pedido
            </button>
          )}
        </div>
      </div>

      <CancelOrderModal
        isOpen={cancelModalOpen}
        orderNumber={order.id}
        onConfirm={handleCancelConfirm}
        onCancel={() => !cancelLoading && setCancelModalOpen(false)}
        loading={cancelLoading}
      />
    </MainLayout>
  );
}

/* ── Status Timeline ─────────────────────────────────────────── */

const CANCEL_STATUSES = [4, 5, 6];

// Fallback labels for steps not yet reached (no history entry)
const STEP_LABELS: Record<number, string> = {
  2: 'Pendiente de entrega',
  3: 'Entregado',
  4: 'Cancelado',
};

function StatusTimeline({ history, currentStatus }: { history: StatusHistoryEntry[]; currentStatus: number | null }) {
  const isCanceled = currentStatus !== null && CANCEL_STATUSES.includes(currentStatus);
  const historyStatuses = history.map(h => h.status);

  // Base flow: [2, 3]. Prepend step 1 only if the order actually went through it.
  const expectedFlow = historyStatuses.includes(1) ? [1, 2, 3] : [2, 3];

  let steps: number[];
  if (isCanceled) {
    // Show history steps that are in the expected flow, then cancel steps.
    // If pending cancelation (5), also show Cancelado (4) as the final destination.
    const passedSteps = expectedFlow.filter(s => historyStatuses.includes(s));
    const cancelStatus = historyStatuses.find(s => CANCEL_STATUSES.includes(s));
    if (cancelStatus === 5) {
      // Pending cancelation → show it, then Cancelado as future step
      steps = [...passedSteps, 5, 4];
    } else if (cancelStatus) {
      steps = [...passedSteps, cancelStatus];
    } else {
      steps = passedSteps;
    }
  } else {
    // Show expected flow (past + future milestones)
    steps = expectedFlow;
  }

  // Deduplicate (keep first occurrence)
  const uniqueSteps = [...new Set(steps)];

  return (
    <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between overflow-x-auto">
        {uniqueSteps.map((statusCode, i) => {
          const historyEntry = history.find(h => h.status === statusCode);
          const isActive = currentStatus === statusCode;
          const isPast = historyEntry && !isActive;
          const isCancelStep = CANCEL_STATUSES.includes(statusCode);

          let dotColor = 'bg-gray-300';
          let textColor = 'text-gray-400';
          if (isActive && isCancelStep) {
            dotColor = 'bg-red-500';
            textColor = 'text-red-700';
          } else if (isActive) {
            dotColor = 'bg-green-500';
            textColor = 'text-green-700';
          } else if (isPast) {
            dotColor = 'bg-green-500';
            textColor = 'text-gray-600';
          }

          // Use the status_name from the history entry, or the step label for future steps
          const label = historyEntry?.status_name ?? STEP_LABELS[statusCode] ?? '';

          const isLast = i === uniqueSteps.length - 1;

          return (
            <div key={statusCode} className={`flex items-start ${isLast ? 'flex-shrink-0' : 'flex-1'}`}>
              <div className="flex flex-col items-center text-center">
                <div className={`h-3 w-3 rounded-full ${dotColor} flex-shrink-0`} />
                <span className={`mt-1.5 text-[11px] font-medium leading-tight ${textColor}`}>
                  {label}
                </span>
                {historyEntry && (
                  <span className="mt-0.5 text-[10px] text-gray-400">
                    {historyEntry.date} {historyEntry.time}
                  </span>
                )}
              </div>
              {!isLast && (
                <div className={`mx-2 mt-[5px] h-px flex-1 ${isPast || isActive ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Info Item ────────────────────────────────────────────────── */

function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex-shrink-0 text-gray-400">{icon}</span>
      <div>
        <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">{label}</span>
        <p className="mt-0.5 text-sm text-gray-800">{value}</p>
      </div>
    </div>
  );
}
